<template>
  <main class="dashboard-page">
    <!-- Hero 大盘 -->
    <section class="panel hero-panel">
      <div class="hero-head">
        <div>
          <h2 class="hero-title">{{ heroTitle }}</h2>
        </div>
      </div>
      <div class="hero-metrics">
        <div class="hero-primary">
          <div class="hero-sub-text">{{ heroMegaLabel }}</div>
          <div class="hero-mega">{{ $formatNumber(heroPeriod.tokens) }}</div>
          <div class="hero-money">¥ {{ $formatNumber(heroPeriod.cost) }} 计费成本</div>
          <div class="hero-tabs">
            <button v-for="t in heroTabs" :key="t.key" type="button"
                    class="hero-tab" :class="{ active: heroTab === t.key }"
                    @click="heroTab = t.key">{{ t.label }}</button>
          </div>
        </div>
        <div class="hero-side">
          <div class="value-row">
            <div>
              <small>近 7 天用量</small>
              <strong>{{ $formatNumber(hero.week ? hero.week.tokens : 0) }}</strong>
            </div>
            <span class="tag red">7d</span>
          </div>
          <div class="value-row">
            <div>
              <small>近 30 天用量</small>
              <strong>{{ $formatNumber(hero.month ? hero.month.tokens : 0) }}</strong>
            </div>
            <span class="tag blue">30d</span>
          </div>
          <div class="value-row">
            <div>
              <small>日均消耗</small>
              <strong>{{ $formatNumber(dailyAvg) }}</strong>
            </div>
            <span class="tag green">日均</span>
          </div>
        </div>
        <div class="hero-side">
          <div class="value-row">
            <div>
              <small>当日 Token</small>
              <strong>{{ $formatNumber(hero.day ? hero.day.tokens : 0) }}</strong>
            </div>
            <span class="tag blue">DAY</span>
          </div>
          <div class="value-row">
            <div>
              <small>当日成本</small>
              <strong>¥ {{ $formatNumber(hero.day ? hero.day.cost : 0) }}</strong>
            </div>
            <span class="tag amber">成本</span>
          </div>
          <div class="value-row">
            <div>
              <small>近 7 天成本</small>
              <strong>¥ {{ $formatNumber(hero.week ? hero.week.cost : 0) }}</strong>
            </div>
            <span class="tag green">7d</span>
          </div>
        </div>
      </div>
    </section>

    <!-- KPI 卡片 -->
    <section class="grid kpis" :class="{ five: cards.length === 5 }">
      <div class="card" v-for="card in cards" :key="card.label">
        <div class="card-head">
          <span>{{ card.label }}</span>
          <span class="live-tag">LIVE</span>
        </div>
        <div class="metric">
          {{ formatMetric(card) }}
          <small v-if="unitFor(card)">{{ unitFor(card) }}</small>
        </div>
        <div v-if="card.delta != null" class="delta" :class="deltaClass(card.delta)">
          {{ deltaText(card.delta) }}
          <small v-if="card.deltaLabel" class="delta-label">{{ card.deltaLabel }}</small>
        </div>
        <div v-else class="delta muted-text">
          <small>{{ card.deltaLabel || '—' }}</small>
        </div>
      </div>
    </section>

    <!-- 用量排行 + 费用占比 -->
    <section class="grid two">
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">{{ usageTitle }}</h3>
          <span class="panel-sub">{{ periodLabel }}</span>
        </div>
        <div class="depth">
          <div class="depth-row" v-for="row in tokenUsage" :key="row.name">
            <b>{{ row.name }}</b>
            <div class="depth-bar"><span :style="{ width: depthPct(row.tokens, tokenUsage) + '%' }"></span></div>
            <strong>{{ $formatNumber(row.tokens) }}</strong>
          </div>
        </div>
      </div>
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">{{ costTitle }}</h3>
          <span class="panel-sub">{{ periodLabel }}</span>
        </div>
        <div ref="pieChart" class="chart-container" />
      </div>
    </section>

    <!-- 请求趋势 + 路由健康 -->
    <section class="grid two">
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">请求趋势</h3>
          <span class="panel-sub">近 24 小时</span>
        </div>
        <div ref="trendChart" class="chart-container" />
      </div>
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">路由健康</h3>
          <div class="panel-head-right">
            <span class="panel-sub">近 24 小时</span>
            <span class="tag" :class="routeHealthTagClass">{{ routeHealthSummary }}</span>
          </div>
        </div>
        <div class="split-list">
          <div class="list-line" v-for="r in routeHealth" :key="r.code">
            <div>
              <strong>{{ r.name }}</strong>
              <small>{{ routeHealthDesc(r) }}</small>
            </div>
            <span class="tag" :class="routeStatusClass(r.status)">{{ routeStatusText(r.status) }}</span>
          </div>
          <div v-if="!routeHealth.length" class="list-line muted-text"><div><strong>暂无数据</strong></div></div>
        </div>
      </div>
    </section>

    <!-- 热力图 -->
    <section class="panel">
      <div class="panel-head">
        <h3 class="panel-title">调用时段热力图</h3>
        <span class="panel-sub">近 7 天 / 7×24 时段分布</span>
      </div>
      <div class="heatmap-block">
        <div class="heatmap-hours">
          <span class="heatmap-corner"></span>
          <span v-for="h in 24" :key="h" class="hour-label">{{ ((h - 1) % 3 === 0) ? (h - 1) : '' }}</span>
        </div>
        <div class="heatmap-body">
          <div class="heatmap-weekdays">
            <span v-for="(d, i) in weekdays" :key="i" class="weekday-label">{{ d }}</span>
          </div>
          <div class="heatmap-grid">
            <div v-for="cell in heatmapCells" :key="cell.dayOfWeek + '-' + cell.hour"
                 class="heat-cell" :style="heatStyle(cell)"
                 :title="heatTooltip(cell)"></div>
          </div>
        </div>
      </div>
      <div class="heat-legend">
        <span>低活跃</span>
        <div class="heat-scale">
          <i></i><i></i><i></i><i></i><i></i>
        </div>
        <span>高活跃 / 峰值</span>
      </div>
    </section>
  </main>
</template>

<script>
import * as echarts from 'echarts'

const HERO_TABS = [
  { key: 'day', label: 'DAY' },
  { key: 'week', label: 'WEEK' },
  { key: 'month', label: 'MONTH' },
  { key: 'total', label: 'TOTAL' }
]

const HERO_TITLES = {
  admin: '企业 Token 资产大盘',
  customer: '客户 Token 资产大盘',
  personal: '个人 Token 用量大盘'
}
const HERO_MEGA_LABELS = {
  day: '当日 Token 消耗',
  week: '近 7 天 Token 消耗',
  month: '近 30 天 Token 消耗',
  total: '累计 Token 消耗'
}

export default {
  name: 'DashboardPage',
  data() {
    return {
      dashboard: {
        type: '',
        cards: [],
        tokenUsage: [],
        costUsage: [],
        hero: null,
        routeHealth: [],
        heatmap: [],
        requestTrend: []
      },
      heroTab: 'month',
      pieChart: null,
      trendChart: null
    }
  },
  computed: {
    heroTabs() { return HERO_TABS },
    cards() { return this.dashboard.cards || [] },
    tokenUsage() { return (this.dashboard.tokenUsage || {})[this.heroTab] || [] },
    costUsage() { return (this.dashboard.costUsage || {})[this.heroTab] || [] },
    hero() {
      return this.dashboard.hero || { totalTokens: 0, totalCost: 0, day: {}, week: {}, month: {}, total: {} }
    },
    heroPeriod() {
      const p = this.dashboard.hero
      if (!p) return { tokens: 0, cost: 0 }
      return p[this.heroTab] || { tokens: 0, cost: 0 }
    },
    heroTitle() { return HERO_TITLES[this.dashboard.type] || 'AI 网关大盘' },
    heroMegaLabel() { return HERO_MEGA_LABELS[this.heroTab] || 'Token 消耗' },
    usageTitle() {
      const map = { admin: '客户 Token 用量排行', customer: '账号 Token 用量排行', personal: '模型 Token 用量排行' }
      return map[this.dashboard.type] || 'Token 用量排行'
    },
    costTitle() {
      const map = { admin: '客户费用占比', customer: '账号费用占比', personal: '模型费用占比' }
      return map[this.dashboard.type] || '费用占比'
    },
    periodLabel() {
      return { day: '当日', week: '近 7 天', month: '近 30 天', total: '累计' }[this.heroTab] || ''
    },
    dailyAvg() {
      const month = this.hero.month ? this.hero.month.tokens : 0
      return Math.max(0, Math.round((month || 0) / 30))
    },
    routeHealth() { return this.dashboard.routeHealth || [] },
    routeHealthSummary() {
      const list = this.routeHealth
      if (!list.length) return '无供应商'
      const online = list.filter(r => r.status === 'online' || r.status === 'idle').length
      return `${online}/${list.length} 在线`
    },
    routeHealthTagClass() {
      const list = this.routeHealth
      if (!list.length) return 'gray'
      const hasBreak = list.some(r => r.status === 'circuit_break')
      const hasDegraded = list.some(r => r.status === 'degraded')
      if (hasBreak) return 'red'
      if (hasDegraded) return 'amber'
      return 'green'
    },
    heatmap() { return this.dashboard.heatmap || [] },
    heatmapCells() {
      if (!this.heatmap.length) {
        return Array.from({ length: 168 }, (_, i) => ({
          dayOfWeek: Math.floor(i / 24),
          hour: i % 24,
          calls: 0,
          tokens: 0
        }))
      }
      return this.heatmap
    },
    heatMaxCalls() {
      return this.heatmapCells.reduce((m, c) => Math.max(m, c.calls || 0), 0)
    },
    weekdays() {
      return ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
    }
  },
  async mounted() {
    await this.fetchDashboard()
  },
  watch: {
    heroTab() {
      this.$nextTick(() => this.renderPieChart(this.costUsage))
    }
  },
  beforeDestroy() {
    if (this.pieChart) this.pieChart.dispose()
    if (this.trendChart) this.trendChart.dispose()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async fetchDashboard() {
      try {
        this.dashboard = await this.$http.get('/admin/dashboard')
        this.$nextTick(() => this.renderCharts())
      } catch {
        this.$message.error('加载首页数据失败')
      }
    },
    formatMetric(card) {
      const isMoney = card.label.includes('金额') || card.label.includes('余额')
      if (isMoney) return '¥ ' + this.$formatNumber(card.value)
      if (card.label.includes('余量')) return Number(card.value || 0)
      return this.$formatNumber(card.value)
    },
    unitFor(card) {
      const label = card.label
      if (label.includes('金额') || label.includes('余额')) return '元'
      if (label.includes('余量')) return '次'
      if (label.includes('客户数')) return '家'
      if (label.includes('模型数')) return '个'
      if (label.includes('Token')) return 'tokens'
      if (label.includes('次数')) return '次'
      return ''
    },
    deltaClass(delta) {
      if (delta == null) return 'neutral'
      if (delta > 0) return 'up'
      if (delta < 0) return 'down'
      return 'neutral'
    },
    deltaText(delta) {
      if (delta == null) return '—'
      const sign = delta > 0 ? '↑ +' : delta < 0 ? '↓ ' : ''
      return sign + Math.abs(delta).toFixed(1) + '%'
    },
    depthPct(value, list) {
      const max = list.reduce((m, x) => Math.max(m, Number(x.tokens || 0)), 0)
      return max > 0 ? Math.round((Number(value || 0) / max) * 100) : 0
    },
    routeHealthDesc(r) {
      const parts = []
      if (r.latencyMs != null) parts.push(`平均 ${Math.round(r.latencyMs)}ms`)
      if (r.failureRate != null) parts.push(`失败率 ${r.failureRate.toFixed(2)}%`)
      if (r.totalRequests != null) parts.push(`${r.totalRequests} 次`)
      return parts.join(' / ') || '暂无调用'
    },
    routeStatusClass(status) {
      return { online: 'green', idle: 'gray', degraded: 'amber', circuit_break: 'red' }[status] || 'gray'
    },
    routeStatusText(status) {
      return { online: '在线', idle: '空闲', degraded: '降级', circuit_break: '熔断' }[status] || status
    },
    heatStyle(cell) {
      const calls = cell.calls || 0
      const ratio = this.heatMaxCalls > 0 ? calls / this.heatMaxCalls : 0
      let color
      if (calls === 0) color = 'rgba(79, 134, 255, 0.06)'
      else if (ratio < 0.25) color = 'rgba(79, 134, 255, 0.35)'
      else if (ratio < 0.5) color = 'rgba(50, 200, 245, 0.55)'
      else if (ratio < 0.75) color = 'rgba(22, 217, 152, 0.7)'
      else color = 'rgba(255, 176, 32, 0.85)'
      return { background: color }
    },
    heatTooltip(cell) {
      const wd = this.weekdays[cell.dayOfWeek] || ''
      const hh = String(cell.hour).padStart(2, '0')
      const calls = cell.calls || 0
      const tokens = this.$formatNumber(cell.tokens || 0)
      return `${wd} ${hh}:00\n调用 ${calls} 次 / Token ${tokens}`
    },
    renderCharts() {
      this.renderPieChart(this.costUsage)
      this.renderTrendChart(this.dashboard.requestTrend || [])
      window.addEventListener('resize', this.handleResize)
    },
    handleResize() {
      if (this.pieChart) this.pieChart.resize()
      if (this.trendChart) this.trendChart.resize()
    },
    renderPieChart(data) {
      if (!this.$refs.pieChart) return
      if (this.pieChart) this.pieChart.dispose()
      this.pieChart = echarts.init(this.$refs.pieChart)
      const colors = ['#4f86ff', '#32c8f5', '#34c98f', '#f4b74f', '#8b78ff', '#ef6d7a']
      const total = data.reduce((s, x) => s + Number(x.amount || 0), 0) || 1
      const items = data.map((d, i) => ({
        name: d.name,
        value: Number(d.amount || 0),
        itemStyle: { color: colors[i % colors.length] }
      }))
      this.pieChart.setOption({
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          backgroundColor: '#1a2638',
          borderColor: 'rgba(151, 171, 207, 0.18)',
          textStyle: { color: '#e7eef8' },
          formatter: p => `${p.name}<br/>¥ ${this.$formatNumber(p.value)} (${((p.value / total) * 100).toFixed(1)}%)`
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 10,
          top: 'center',
          textStyle: { color: '#9aaac1', fontSize: 12 },
          pageTextStyle: { color: '#9aaac1' }
        },
        series: [{
          type: 'pie',
          radius: ['52%', '72%'],
          center: ['38%', '50%'],
          avoidLabelOverlap: false,
          label: {
            show: true,
            position: 'center',
            formatter: () => `¥ ${this.$formatNumber(total)}\n总成本`,
            color: '#e7eef8',
            fontSize: 14,
            fontWeight: 800,
            lineHeight: 22
          },
          labelLine: { show: false },
          emphasis: {
            label: { show: true, fontWeight: 800, fontSize: 15 },
            itemStyle: { shadowBlur: 16, shadowColor: 'rgba(50, 200, 245, 0.3)' }
          },
          data: items
        }]
      })
      this.pieChart.resize()
    },
    renderTrendChart(data) {
      if (!this.$refs.trendChart) return
      if (this.trendChart) this.trendChart.dispose()
      this.trendChart = echarts.init(this.$refs.trendChart)
      const times = data.map(d => d.time)
      const values = data.map(d => d.value)
      this.trendChart.setOption({
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#1a2638',
          borderColor: 'rgba(151, 171, 207, 0.18)',
          textStyle: { color: '#e7eef8' }
        },
        grid: { left: 40, right: 20, top: 20, bottom: 30, containLabel: true },
        xAxis: {
          type: 'category',
          data: times,
          boundaryGap: false,
          axisLine: { lineStyle: { color: 'rgba(151, 171, 207, 0.18)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#9aaac1', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(151, 171, 207, 0.1)', type: 'dashed' } }
        },
        series: [{
          type: 'line',
          data: values,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: false,
          lineStyle: {
            color: '#32c8f5',
            width: 2.6,
            shadowColor: 'rgba(50, 200, 245, 0.6)',
            shadowBlur: 10
          },
          itemStyle: { color: '#32c8f5', borderColor: '#101927', borderWidth: 2 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(50, 200, 245, 0.5)' },
              { offset: 1, color: 'rgba(50, 200, 245, 0)' }
            ])
          }
        }]
      })
      this.trendChart.resize()
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  display: grid;
  gap: 16px;
  margin: 0 24px;
}

/* ---------- Hero 大盘 ---------- */
.hero-panel {
  position: relative;
  overflow: hidden;
  padding: 22px;
  background: linear-gradient(135deg, rgba(27, 41, 62, 0.94), rgba(19, 30, 47, 0.92));
  border-color: rgba(79, 134, 255, 0.22);
}
.hero-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 18% 20%, rgba(50, 200, 245, 0.1), transparent 28%),
    radial-gradient(circle at 84% 12%, rgba(139, 120, 255, 0.12), transparent 26%);
  pointer-events: none;
}
.hero-panel > * { position: relative; z-index: 1; }
.hero-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}
.hero-title {
  margin: 10px 0 0;
  font-size: 22px;
  font-weight: 860;
  color: var(--text);
}
.hero-sub {
  color: var(--muted);
  font-size: 12px;
}
.hero-sub-text {
  color: var(--muted);
  font-size: 12px;
  margin-bottom: 6px;
}
.hero-metrics {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr 0.9fr;
  gap: 16px;
  align-items: stretch;
}
.hero-primary {
  padding: 20px;
  border: 1px solid rgba(50, 200, 245, 0.14);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
}
.hero-mega {
  color: #eef5fb;
  font-size: clamp(28px, 3.4vw, 56px);
  line-height: 1;
  font-weight: 900;
  text-shadow: 0 0 18px rgba(50, 200, 245, 0.14);
  overflow-wrap: anywhere;
}
.hero-money {
  margin-top: 10px;
  color: #f0c96b;
  font-family: Consolas, "SFMono-Regular", monospace;
  font-size: 18px;
  font-weight: 900;
}
.hero-tabs {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.hero-tab {
  padding: 6px 12px;
  border: 1px solid rgba(50, 200, 245, 0.28);
  border-radius: 6px;
  background: transparent;
  color: #8eddf8;
  font-size: 11px;
  font-weight: 900;
  cursor: pointer;
  letter-spacing: 0.04em;
}
.hero-tab.active {
  background: rgba(50, 200, 245, 0.18);
  color: #fff;
  border-color: rgba(50, 200, 245, 0.6);
}
.hero-side {
  display: grid;
  gap: 12px;
}
.value-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;
  min-height: 74px;
  border: 1px solid rgba(133, 164, 255, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
}
.value-row strong {
  display: block;
  font-size: clamp(18px, 1.8vw, 26px);
  overflow-wrap: anywhere;
  color: var(--text);
  font-weight: 900;
}
.value-row small {
  display: block;
  margin-top: 5px;
  color: var(--muted);
  font-size: 11px;
}

/* ---------- KPI 卡片 ---------- */
.kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 16px;
}
.kpis.five {
  grid-template-columns: repeat(5, minmax(140px, 1fr));
}
.card {
  min-height: 116px;
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
  position: relative;
  overflow: hidden;
}
.card::after {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(50, 200, 245, 0.78), transparent 70%);
  opacity: 0.5;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--muted);
  font-size: 13px;
  font-weight: 760;
}
.live-tag {
  color: var(--cyan);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.04em;
}
.metric {
  margin-top: 10px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  max-width: 100%;
  font-size: clamp(20px, 2.2vw, 28px);
  line-height: 1.05;
  font-weight: 900;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: var(--text);
}
.metric small {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}
.delta {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 850;
}
.delta.up { color: var(--red); }
.delta.down { color: var(--green); }
.delta.neutral, .muted-text { color: var(--muted); }
.delta-label {
  margin-left: 6px;
  color: var(--muted);
  font-weight: 700;
}

/* ---------- 通用 panel ---------- */
.panel {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.panel-head-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 880;
  color: var(--text);
}
.panel-sub {
  color: var(--muted);
  font-size: 12px;
}

.grid {
  display: grid;
  gap: 16px;
}
.grid.two {
  grid-template-columns: 1fr 1fr;
}
.grid.three {
  grid-template-columns: repeat(3, 1fr);
}

/* ---------- 用量排行 ---------- */
.depth {
  display: grid;
  gap: 12px;
}
.depth-row {
  display: grid;
  grid-template-columns: 110px 1fr 90px;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--text);
}
.depth-row b {
  font-weight: 800;
  color: #c6d4e9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.depth-bar {
  height: 10px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.depth-bar span {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, var(--cyan), var(--blue));
  box-shadow: 0 0 12px rgba(50, 200, 245, 0.3);
  transition: width 0.4s ease;
}
.depth-row strong {
  text-align: right;
  font-weight: 850;
  color: var(--text);
}

/* ---------- 路由健康 split-list ---------- */
.split-list {
  display: grid;
  gap: 10px;
}
.list-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(133, 164, 255, 0.12);
}
.list-line strong {
  font-size: 13px;
  color: var(--text);
}
.list-line small {
  color: var(--muted);
  display: block;
  margin-top: 4px;
  font-size: 11px;
}

/* ---------- 图表容器 ---------- */
.chart-container {
  width: 100%;
  height: 240px;
}

/* ---------- 热力图 ---------- */
.heatmap-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.heatmap-hours {
  display: grid;
  grid-template-columns: 36px repeat(24, 1fr);
  gap: 3px;
  height: 16px;
}
.heatmap-corner {
  background: transparent;
}
.hour-label {
  font-size: 10px;
  color: var(--muted);
  text-align: center;
  line-height: 16px;
  font-weight: 800;
}
.heatmap-body {
  display: flex;
  gap: 4px;
}
.heatmap-weekdays {
  display: grid;
  grid-template-rows: repeat(7, 22px);
  gap: 3px;
  width: 36px;
  flex: none;
}
.weekday-label {
  font-size: 11px;
  color: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}
.heatmap-grid {
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-template-rows: repeat(7, 22px);
  gap: 3px;
  flex: 1;
  min-width: 0;
}
.heat-cell {
  border-radius: 3px;
  min-width: 0;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}
.heat-cell:hover {
  transform: scale(1.25);
  box-shadow: 0 0 0 1px rgba(50, 200, 245, 0.8), 0 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 2;
}
.heat-legend {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  color: #8fa2bb;
  font-size: 11px;
}
.heat-scale {
  display: flex;
  gap: 4px;
  align-items: center;
}
.heat-scale i {
  width: 18px;
  height: 8px;
  border-radius: 99px;
  display: inline-block;
}
.heat-scale i:nth-child(1) { background: rgba(79, 134, 255, 0.06); }
.heat-scale i:nth-child(2) { background: rgba(79, 134, 255, 0.35); }
.heat-scale i:nth-child(3) { background: rgba(50, 200, 245, 0.55); }
.heat-scale i:nth-child(4) { background: rgba(22, 217, 152, 0.7); }
.heat-scale i:nth-child(5) { background: rgba(255, 176, 32, 0.85); }

/* ---------- 响应式 ---------- */
@media (max-width: 1200px) {
  .dashboard-page {
    margin: 0 16px;
  }
  .hero-metrics {
    grid-template-columns: 1fr;
  }
  .kpis, .kpis.five {
    grid-template-columns: repeat(2, 1fr);
  }
  .grid.two {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .dashboard-page {
    margin: 0 12px;
  }
  .heatmap-weekdays {
    width: 28px;
    grid-template-rows: repeat(7, 16px);
  }
  .heatmap-hours {
    grid-template-columns: 28px repeat(24, 1fr);
    height: 12px;
  }
  .hour-label {
    font-size: 8px;
    line-height: 12px;
  }
  .weekday-label {
    font-size: 9px;
  }
  .heatmap-grid {
    grid-template-rows: repeat(7, 16px);
  }
  .kpis, .kpis.five {
    grid-template-columns: 1fr;
  }
  .depth-row {
    grid-template-columns: 90px 1fr 70px;
  }
}
</style>
